import { SecurityFeature as SECURITY_FEATURES } from '@dealstack/shared/lib/common/securities/securityFeature';
import { SecurityFeaturesButtonsStyles } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/SecurityFeaturesButtons/SecurityFeaturesButtons.styles.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	IncomeType,
	UpdateSecurityDto,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, List, Tooltip } from '@mantine/core';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { previewFeatureAddition } from '@dealstack/shared/lib/common/securities/securityFeatureRules';
import {
	FeatureAdditionBlockedContent,
	FeatureAdditionCascadeContent,
	FeatureAdditionChange,
	makeFeatureAdditionBlockedContent,
	makeFeatureAdditionCascadeContent,
	securityFeatureBlockTranslation,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { makeSecurityFeatureSnapshot } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatureSnapshot.ts';
import { SecurityWarningModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityWarningModal.tsx';
import { updateSecurity } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useMutation } from '@tanstack/react-query';

interface Props {
	newFeatureRows: Record<SECURITY_FEATURES, boolean>;
	setNewFeatureRows: Dispatch<SetStateAction<Record<SECURITY_FEATURES, boolean>>>;
	security: DataSecurityWithAllFeaturesAndRanksTct | null;
	hasEditorPermission?: boolean;
	refreshDetails?: (securityId: string) => void;
}

type PendingFeatureAdd =
	| {
			feature: SECURITY_FEATURES;
			kind: 'cascade';
			content: FeatureAdditionCascadeContent;
			residueWouldFlipOn: boolean;
			residueWouldFlipOff: boolean;
	  }
	| {
			feature: SECURITY_FEATURES;
			kind: 'blocked';
			content: FeatureAdditionBlockedContent;
	  };

const renderChangeItem = (change: FeatureAdditionChange) => (
	<>
		{change.prefix}
		<strong>{change.emphasis}</strong>
		{change.suffix ?? ''}
	</>
);

const bulletListStyles = {
	item: { listStyleType: 'disc' as const },
};

const renderPendingDescription = (pending: PendingFeatureAdd) =>
	pending.kind === 'cascade' ? (
		<>
			{pending.content.intro}
			<List withPadding mt="0.5rem" spacing="0.25rem" size="md" styles={bulletListStyles}>
				{pending.content.changes.map((change, i) => (
					<List.Item key={i}>{renderChangeItem(change)}</List.Item>
				))}
			</List>
		</>
	) : (
		<>
			{pending.content.intro}
			<List withPadding mt="0.5rem" spacing="0.25rem" size="md" styles={bulletListStyles}>
				{pending.content.reasons.map((reason) => (
					<List.Item key={reason}>{reason}</List.Item>
				))}
			</List>
		</>
	);

// Builds a full UpdateSecurityDto from the current security so a residue-flip write doesn't
// accidentally clear other fields that the endpoint would treat as null. Flipping residue on
// invalidates TRACKING (which requires NO_RESIDUE_PARTICIPATION), so we null incomeType in
// that case — otherwise the backend's assertFeatureAllowed(TRACKING) would reject the write.
const buildResidueFlipPayload = (
	security: DataSecurityWithAllFeaturesAndRanksTct,
	residueRights: boolean
): UpdateSecurityDto => {
	const trackingWillBeDropped = residueRights && security.incomeType === IncomeType.TRACKING;
	// The generated FE DTO type doesn't reflect the backend's nullable incomeType; casting
	// through unknown lets us signal "explicitly clear" (which backend distinguishes from
	// "not sent" via an === undefined check).
	const incomeType = trackingWillBeDropped
		? (null as unknown as IncomeType | undefined)
		: security.incomeType ?? undefined;
	return {
		id: security.id,
		name: security.name,
		type: security.type,
		votesPerUnit: security.votesPerUnit,
		nominalValue: security.nominalValue,
		issuedIn: security.issuedIn,
		equityBasis: security.equityBasis,
		residueRights,
		loanDefinitionId: security.loanDefinitionId,
		unitDenominator: security.unitDenominator,
		incomeType,
	};
};

export const SecurityFeaturesButtons = ({
	security,
	hasEditorPermission,
	newFeatureRows,
	setNewFeatureRows,
	refreshDetails,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [pendingFeature, setPendingFeature] = useState<PendingFeatureAdd | null>(null);
	const updateSecurityRequest = useMutation({
		mutationFn: updateSecurity,
	});

	const openEditorRow = (feature: SECURITY_FEATURES) => {
		setNewFeatureRows((prevState) => ({
			...prevState,
			[feature]: true,
		}));
	};

	const handleFeatureClick = (feature: SECURITY_FEATURES) => () => {
		if (!security) {
			return;
		}
		const snapshot = makeSecurityFeatureSnapshot(security);
		const preview = previewFeatureAddition(feature, snapshot);

		if (preview.hardBlockers.length > 0) {
			setPendingFeature({
				feature,
				kind: 'blocked',
				content: makeFeatureAdditionBlockedContent(
					tCapManage,
					feature,
					preview.hardBlockers
				),
			});
			return;
		}

		if (
			preview.residueWouldFlipOn ||
			preview.residueWouldFlipOff ||
			preview.cascade.features.length ||
			preview.cascade.clearedStates.length
		) {
			setPendingFeature({
				feature,
				kind: 'cascade',
				content: makeFeatureAdditionCascadeContent(
					tCapManage,
					feature,
					preview.cascade,
					preview.residueWouldFlipOn,
					preview.residueWouldFlipOff
				),
				residueWouldFlipOn: preview.residueWouldFlipOn,
				residueWouldFlipOff: preview.residueWouldFlipOff,
			});
			return;
		}

		openEditorRow(feature);
	};

	const closePendingFeature = () => setPendingFeature(null);

	const confirmPendingFeature = () => {
		if (!pendingFeature || !security) {
			return;
		}
		if (pendingFeature.kind !== 'cascade') {
			return;
		}
		const featureToAdd = pendingFeature.feature;

		if (pendingFeature.residueWouldFlipOn || pendingFeature.residueWouldFlipOff) {
			const targetResidue = pendingFeature.residueWouldFlipOn;
			const securityId = security.id;
			updateSecurityRequest.mutate(buildResidueFlipPayload(security, targetResidue), {
				onSuccess: () => {
					// The residue flip already committed the cascade server-side. Refresh security
					// details so removed feature rows disappear immediately, even if the user
					// cancels the editor we're about to open.
					refreshDetails?.(securityId);
					openEditorRow(featureToAdd);
					setPendingFeature(null);
				},
			});
			return;
		}

		openEditorRow(featureToAdd);
		setPendingFeature(null);
	};

	const featureButtons = useMemo(() => {
		const buttons: {
			label: string;
			rightIcon?: React.ReactNode;
			onClick: () => void;
			testId: string;
		}[] = [];
		const showAuthorisedCapitalButton =
			!security?.authorisedCapital && !newFeatureRows[SECURITY_FEATURES.AUTHORISED_CAPITAL];
		const showIncomeRightsInterestButton =
			!security?.securityInterestDefinition &&
			!newFeatureRows[SECURITY_FEATURES.INCOME_RIGHTS_INTEREST];
		const showIncomeRightsTrackingButton =
			security?.incomeType !== IncomeType.TRACKING &&
			!newFeatureRows[SECURITY_FEATURES.INCOME_RIGHTS_TRACKING];
		const showRedemptionRightsButton =
			!security?.securityRedemptionRights &&
			!newFeatureRows[SECURITY_FEATURES.REDEMPTION_RIGHTS];
		const showCatchUpRightsButton =
			!security?.securityCatchUpRights && !newFeatureRows[SECURITY_FEATURES.CATCH_UP_RIGHTS];
		const showExchangeRightsButton =
			!security?.securityExchangeRights && !newFeatureRows[SECURITY_FEATURES.EXCHANGE_RIGHTS];
		const showParticipationThresholdButton =
			!security?.residueParticipationThreshold &&
			!newFeatureRows[SECURITY_FEATURES.PARTICIPATION_THRESHOLD];
		const showRatchetingButton = !newFeatureRows[SECURITY_FEATURES.RATCHETING];
		const showLiquidationPreferenceButton =
			!security?.hasLiquidationPreference &&
			!newFeatureRows[SECURITY_FEATURES.LIQUIDATION_PREFERENCE];

		if (!security || !hasEditorPermission) {
			return buttons;
		}

		const snapshot = makeSecurityFeatureSnapshot(security);
		const ruleTooltip = (feature: SECURITY_FEATURES) => {
			const preview = previewFeatureAddition(feature, snapshot);
			const hardReason = preview.hardBlockers[0];
			const willCascade =
				preview.residueWouldFlipOn ||
				preview.cascade.features.length > 0 ||
				preview.cascade.clearedStates.length > 0;

			if (!hardReason && !willCascade) {
				return undefined;
			}

			const label = hardReason
				? (() => {
						const translation = securityFeatureBlockTranslation(hardReason);
						return tCapManage(translation.key, {
							defaultValue: translation.defaultValue,
						});
					})()
				: tCapManage('securities.feature_rules.will_cascade_tooltip', {
						defaultValue: 'Adding this will change other feature settings.',
					});

			return (
				<Tooltip label={label}>
					<Flex c="dark.8" className="tooltip-icon">
						<Icon variant="information" size="lg" />
					</Flex>
				</Tooltip>
			);
		};

		if (showAuthorisedCapitalButton) {
			buttons.push({
				label: tCapManage('securities.authorisedCapital.title', {
					defaultValue: 'Authorised capital',
				}),
				onClick: handleFeatureClick(SECURITY_FEATURES.AUTHORISED_CAPITAL),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_AUTHORISED_CAPITAL_BUTTON,
			});
		}

		if (showIncomeRightsInterestButton) {
			buttons.push({
				label: tCapManage('securities.incomeRights_interest.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.INCOME_RIGHTS_INTEREST),
				onClick: handleFeatureClick(SECURITY_FEATURES.INCOME_RIGHTS_INTEREST),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_INCOME_RIGHTS_BUTTON,
			});
		}

		if (showIncomeRightsTrackingButton) {
			buttons.push({
				label: tCapManage('securities.incomeRights_tracking.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.INCOME_RIGHTS_TRACKING),
				onClick: handleFeatureClick(SECURITY_FEATURES.INCOME_RIGHTS_TRACKING),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_INCOME_RIGHTS_TRACKING_BUTTON,
			});
		}

		if (showRedemptionRightsButton) {
			buttons.push({
				label: tCapManage('securities.redemptionRights.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.REDEMPTION_RIGHTS),
				onClick: handleFeatureClick(SECURITY_FEATURES.REDEMPTION_RIGHTS),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_REDEMPTION_RIGHTS_BUTTON,
			});
		}

		if (showCatchUpRightsButton) {
			buttons.push({
				label: tCapManage('securities.catch_up_rights.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.CATCH_UP_RIGHTS),
				onClick: handleFeatureClick(SECURITY_FEATURES.CATCH_UP_RIGHTS),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_CATCH_UP_RIGHTS_BUTTON,
			});
		}

		if (showExchangeRightsButton) {
			buttons.push({
				label: tCapManage('securities.exchange_rights.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.EXCHANGE_RIGHTS),
				onClick: handleFeatureClick(SECURITY_FEATURES.EXCHANGE_RIGHTS),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_EXCHANGE_RIGHTS_BUTTON,
			});
		}

		if (showRatchetingButton) {
			buttons.push({
				label: tCapManage('securities.ratcheting.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.RATCHETING),
				onClick: handleFeatureClick(SECURITY_FEATURES.RATCHETING),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_RATCHETING_BUTTON,
			});
		}

		if (showLiquidationPreferenceButton) {
			buttons.push({
				label: tCapManage('securities.liquidation_preference.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.LIQUIDATION_PREFERENCE),
				onClick: handleFeatureClick(SECURITY_FEATURES.LIQUIDATION_PREFERENCE),
				testId: 'cap-create-liquidation-preference-button',
			});
		}

		if (showParticipationThresholdButton) {
			buttons.push({
				label: tCapManage('securities.participationThreshold.title'),
				rightIcon: ruleTooltip(SECURITY_FEATURES.PARTICIPATION_THRESHOLD),
				onClick: handleFeatureClick(SECURITY_FEATURES.PARTICIPATION_THRESHOLD),
				testId: TEST_ELEMENT_IDS.CAP_CREATE_PARTICIPATION_THRESHOLD_BUTTON,
			});
		}

		return buttons;
	}, [security, newFeatureRows, setNewFeatureRows, hasEditorPermission, tCapManage]);

	return (
		<>
			{!!featureButtons.length && hasEditorPermission && (
				<SecurityFeaturesButtonsStyles>
					<Flex
						align="center"
						wrap="wrap"
						gap="0.25rem"
						p="0.5rem"
						className="buttons-container"
					>
						{featureButtons.map(({ label, rightIcon, onClick, testId }) => (
							<Button
								key={label}
								variant="default"
								fw="400"
								rightSection={rightIcon ?? <Icon variant="add" size="lg" />}
								onClick={onClick}
								data-testid={testId}
								className="button"
							>
								{label}
							</Button>
						))}
					</Flex>
				</SecurityFeaturesButtonsStyles>
			)}
			{pendingFeature && (
				<SecurityWarningModal
					description={renderPendingDescription(pendingFeature)}
					onClose={closePendingFeature}
					onRevert={pendingFeature.kind === 'cascade' ? closePendingFeature : undefined}
					onConfirm={
						pendingFeature.kind === 'cascade' ? confirmPendingFeature : undefined
					}
				/>
			)}
		</>
	);
};
